<template>
    <div>
        <!-- Table slot for entity-specific content -->
        <slot name="table" :filteredData="filteredCurrentPageData" :currentPageData="currentPageData"></slot>

        <!-- Pagination Controls -->
        <v-row class="mt-4" align="center">
            <v-col cols="12" sm="6" md="4">
                <v-select
                    v-model="pagination.pageSize"
                    :items="pagination.pageSizeOptions"
                    label="Items per page"
                    dense
                    @update:model-value="onPageSizeChange"
                    :disabled="pagination.isLoading"
                ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="text-center">
                <v-select
                    v-model="pagination.currentPage"
                    :items="pagination.loadedPages"
                    label="Page"
                    dense
                    @update:model-value="onPageSelect"
                    :disabled="pagination.isLoading || pagination.loadedPages.length === 0"
                ></v-select>
            </v-col>
            <v-col cols="12" sm="12" md="4" class="text-right">
                <v-btn
                    :disabled="pagination.currentPage === 1 || pagination.isLoading"
                    variant="text"
                    icon="mdi-chevron-left"
                    @click="onPreviousPage"
                    title="Previous Page"
                ></v-btn>
                <v-btn
                    :disabled="!pagination.hasNext || pagination.isLoading"
                    variant="text"
                    icon="mdi-chevron-right"
                    @click="onNextPage"
                    title="Next Page"
                    :loading="pagination.isLoading"
                ></v-btn>
            </v-col>
        </v-row>

        <!-- Loading indicator -->
        <v-row v-if="pagination.isLoading && !pagination.isInitialLoad" class="mt-2">
            <v-col cols="12" class="text-center">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
                <span class="text-body-2 mt-2">{{ loadingMessage }}</span>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import { debounce } from 'lodash';

interface PaginationState {
    currentPage: number;
    pageSize: number;
    pageSizeOptions: number[];
    hasNext: boolean;
    isLoading: boolean;
    loadedPages: number[];
    isInitialLoad: boolean;
}

export default defineComponent({
    name: 'PaginatedTable',
    props: {
        pagination: {
            type: Object as PropType<PaginationState>,
            required: true
        },
        currentPageData: {
            type: Array as PropType<any[]>,
            required: true
        },
        quickSearchValue: {
            type: String,
            default: ''
        },
        loadingMessage: {
            type: String,
            default: 'Loading more data...'
        }
    },
    emits: ['page-size-change', 'page-select', 'next-page', 'previous-page'],
    setup(props, { emit }) {
        // Debounced search value (300ms delay)
        const debouncedSearchValue = ref('');

        // Debounced function to update search value
        const updateDebouncedSearch = debounce((value: string) => {
            debouncedSearchValue.value = value;
        }, 300);

        // Watch for changes to quickSearchValue and apply debouncing
        watch(
            () => props.quickSearchValue,
            (newValue) => {
                updateDebouncedSearch(newValue);
            },
            { immediate: true }
        );

        // Quick search filtering logic using debounced value
        const filteredCurrentPageData = computed(() => {
            if (!debouncedSearchValue.value) {
                return props.currentPageData;
            }

            const searchKey = debouncedSearchValue.value.trim().toLowerCase();
            return props.currentPageData.filter((item) => {
                return Object.values(item).some((value) => String(value).toLowerCase().includes(searchKey));
            });
        });

        // Event handlers
        const onPageSizeChange = () => {
            emit('page-size-change');
        };

        const onPageSelect = (selectedPage: number) => {
            if (selectedPage) {
                emit('page-select', selectedPage);
            }
        };

        const onNextPage = () => {
            emit('next-page');
        };

        const onPreviousPage = () => {
            emit('previous-page');
        };

        return {
            filteredCurrentPageData,
            onPageSizeChange,
            onPageSelect,
            onNextPage,
            onPreviousPage
        };
    }
});
</script>
